import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { rgApi, firstName } from "@/lib/rgApi";
import CleaningModal from "./CleaningModal";
import EventEditModal from "./EventEditModal";
import MarketDetailsModal from "./MarketDetailsModal";
import ClosedPeriodModal from "./ClosedPeriodModal";

/**
 * Calendar — vue mensuelle du Curieux Refuge.
 *
 * Grille 6 semaines × 7 colonnes. Pour chaque semaine :
 *   • Un STRIP horizontal beige (#A8956F) traverse tous les jours occupés
 *     par la même réservation, affichant `PRÉNOM — N PERS` en uppercase.
 *   • Des BADGES emoji colorés sont posés SOUS les strips, un par action
 *     à faire ce jour-là : 🧺 marché, 🌸 bain fleurs, 🌹 décor, 🥐 petit-déj,
 *     🧹 ménage (jour d'arrivée).
 *   • Le badge 🧹 ouvre la modale "Ménage unifiée" (préparation + assistant).
 *   • Les autres badges ouvrent la modale d'édition d'horaire.
 */

const WEEKDAYS = ["L", "M", "M", "J", "V", "S", "D"];
const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }
function toIso(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function daysBetween(a, b) {
  // whole day diff (b - a) ignoring DST
  const ms = new Date(b.getFullYear(), b.getMonth(), b.getDate()) - new Date(a.getFullYear(), a.getMonth(), a.getDate());
  return Math.round(ms / 86400000);
}

export default function Calendar() {
  const [ref, setRef] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [data, setData] = useState(null);
  const [cleaningRes, setCleaningRes] = useState(null); // { reservation, cleaningId, done }
  const [editEvent, setEditEvent] = useState(null); // { kind, event, reservation }
  const [marketEvent, setMarketEvent] = useState(null); // { event, reservation }
  const [closedEdit, setClosedEdit] = useState(null); // period object OR "new" for creation
  const nav = useNavigate();

  const from = toIso(new Date(ref.getFullYear(), ref.getMonth(), 1));
  const to = toIso(new Date(ref.getFullYear(), ref.getMonth() + 1, 0));

  const load = useCallback(async () => {
    try {
      const d = await rgApi.calendarEvents(from, to);
      setData(d);
    } catch (e) {
      if (e.status === 401) nav("/admin");
    }
  }, [from, to, nav]);

  useEffect(() => { load(); }, [load]);

  // Grille de 6 semaines (Monday-first)
  const weeks = useMemo(() => {
    const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(first.getDate() - startOffset);
    const arr = [];
    for (let w = 0; w < 6; w++) {
      const days = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + w * 7 + i);
        days.push(d);
      }
      arr.push(days);
    }
    return arr;
  }, [ref]);

  const marketSet = useMemo(() => new Set(data?.market_days || []), [data]);
  const today = toIso(new Date());

  // Index actions par jour et par kind
  const dayEvents = useMemo(() => {
    const m = {};
    const push = (iso, ev) => { (m[iso] ||= []).push(ev); };
    (data?.actions || []).forEach((a) => push(a.date, { ...a, kind: "marche", icon: "🧺" }));
    (data?.cleaning_days || []).forEach((c) => push(c.date, { ...c, kind: "menage", icon: "🧹" }));
    (data?.breakfasts || []).forEach((b) => push(b.date, { ...b, kind: "breakfast", icon: "🥐" }));
    (data?.experiences || []).forEach((x) => push(x.date, { ...x, kind: x.extra_key, icon: x.icon }));
    (data?.troisieme_lit_days || []).forEach((t) => push(t.date, { ...t, kind: "troisieme_lit", icon: "🛏️" }));
    return m;
  }, [data]);

  // Index iCloud PERSO par ISO (petits événements personnels en BAS de case).
  // Les OFF sont traités séparément comme des strips en zone 1.
  const persoByDay = useMemo(() => {
    const m = {};
    (data?.icloud_events || []).forEach((ev) => {
      if (ev.kind === "OFF") return; // OFF → géré comme un strip zone 1
      try {
        const s = new Date(ev.date_start + "T00:00:00");
        const e = new Date((ev.date_end || ev.date_start) + "T00:00:00");
        for (let cur = new Date(s); cur <= e; cur.setDate(cur.getDate() + 1)) {
          const iso = toIso(cur);
          (m[iso] ||= []).push(ev);
        }
      } catch (_e) { /* skip */ }
    });
    // Tri chronologique par heure de début (si dispo), sinon "toute la journée" en premier
    Object.keys(m).forEach((iso) => {
      m[iso].sort((a, b) => {
        const ha = a.time_start || "";
        const hb = b.time_start || "";
        if (!ha && hb) return -1;
        if (ha && !hb) return 1;
        return ha.localeCompare(hb);
      });
    });
    return m;
  }, [data]);

  // Ensemble des jours "occupés" (résa OU fermeture) pour afficher LIBRE en fond
  const occupiedDays = useMemo(() => {
    const s = new Set();
    (data?.reservations || []).forEach((r) => {
      try {
        const a = new Date(r.date_arrival + "T00:00:00");
        const d = new Date(r.date_departure + "T00:00:00");
        // Occuper les nuits [arrival, departure) exclusif
        for (let cur = new Date(a); cur < d; cur.setDate(cur.getDate() + 1)) {
          s.add(toIso(cur));
        }
      } catch (_e) { /* skip */ }
    });
    (data?.closed_periods || []).forEach((cp) => {
      try {
        const a = new Date(cp.date_start + "T00:00:00");
        const d = new Date(cp.date_end + "T00:00:00");
        for (let cur = new Date(a); cur <= d; cur.setDate(cur.getDate() + 1)) {
          s.add(toIso(cur));
        }
      } catch (_e) { /* skip */ }
    });
    return s;
  }, [data]);

  // Construit les strips par semaine à partir des réservations (16h → 12h)
  const weekStrips = useMemo(() => {
    if (!data) return weeks.map(() => []);
    const resas = data.reservations || [];
    const closed = data.closed_periods || [];
    const offEvents = (data.icloud_events || []).filter((ev) => ev.kind === "OFF");
    return weeks.map((weekDays) => {
      const weekStart = weekDays[0];
      const weekEnd = weekDays[6];
      const stripsInWeek = [];

      // Réservations : strip visible arrival → departure INCLUS (offsets 50% aux extrémités)
      resas.forEach((r) => {
        try {
          const arr = new Date(r.date_arrival + "T00:00:00");
          const dep = new Date(r.date_departure + "T00:00:00");
          if (dep < arr) return;
          const overlapStart = arr > weekStart ? arr : weekStart;
          const overlapEnd = dep < weekEnd ? dep : weekEnd;
          if (overlapStart > overlapEnd) return;
          const startCol = daysBetween(weekStart, overlapStart);
          const spanCols = daysBetween(overlapStart, overlapEnd) + 1;
          stripsInWeek.push({
            kind: "resa",
            resa: r,
            startCol,
            spanCols,
            isStart: overlapStart.getTime() === arr.getTime(),
            isEnd: overlapEnd.getTime() === dep.getTime(),
          });
        } catch (_e) { /* skip */ }
      });

      // Fermetures : strip pleine largeur sur toute la période (inclus)
      closed.forEach((cp) => {
        try {
          const cs = new Date(cp.date_start + "T00:00:00");
          const ce = new Date(cp.date_end + "T00:00:00");
          if (ce < cs) return;
          const overlapStart = cs > weekStart ? cs : weekStart;
          const overlapEnd = ce < weekEnd ? ce : weekEnd;
          if (overlapStart > overlapEnd) return;
          const startCol = daysBetween(weekStart, overlapStart);
          const spanCols = daysBetween(overlapStart, overlapEnd) + 1;
          stripsInWeek.push({
            kind: "closed",
            closed: cp,
            startCol,
            spanCols,
            isStart: overlapStart.getTime() === cs.getTime(),
            isEnd: overlapEnd.getTime() === ce.getTime(),
          });
        } catch (_e) { /* skip */ }
      });

      // OFF iCloud : strip zone 1 (fermeture professionnelle du refuge, jamais mélangé aux PERSO)
      offEvents.forEach((ev) => {
        try {
          const os = new Date(ev.date_start + "T00:00:00");
          const oe = new Date((ev.date_end || ev.date_start) + "T00:00:00");
          if (oe < os) return;
          const overlapStart = os > weekStart ? os : weekStart;
          const overlapEnd = oe < weekEnd ? oe : weekEnd;
          if (overlapStart > overlapEnd) return;
          const startCol = daysBetween(weekStart, overlapStart);
          const spanCols = daysBetween(overlapStart, overlapEnd) + 1;
          stripsInWeek.push({
            kind: "off",
            off: ev,
            startCol,
            spanCols,
            isStart: overlapStart.getTime() === os.getTime(),
            isEnd: overlapEnd.getTime() === oe.getTime(),
          });
        } catch (_e) { /* skip */ }
      });

      // Stacking corrigé : deux résas se succédant le MÊME jour (départ 12h → arrivée 16h)
      // partagent la même ligne visuelle car les offsets +HALF/-HALF les séparent réellement.
      // On stocke {endCol, endsWithDeparture} pour chaque slot.
      stripsInWeek.sort((a, b) => a.startCol - b.startCol);
      const rows = [];
      stripsInWeek.forEach((s) => {
        let slot = 0;
        while (true) {
          const prev = rows[slot];
          if (prev === undefined) break;
          if (prev.endCol < s.startCol) break;
          // Cas départ/arrivée le même jour : les deux moitiés (12h / 16h) ne se chevauchent pas.
          if (
            prev.endCol === s.startCol &&
            prev.endsWithDeparture &&
            s.kind === "resa" && s.isStart
          ) break;
          slot += 1;
        }
        rows[slot] = {
          endCol: s.startCol + s.spanCols - 1,
          endsWithDeparture: s.kind === "resa" && s.isEnd,
        };
        s.stackIdx = slot;
      });
      return stripsInWeek;
    });
  }, [data, weeks]);

  const prevMonth = () => { const d = new Date(ref); d.setMonth(d.getMonth() - 1); setRef(d); };
  const nextMonth = () => { const d = new Date(ref); d.setMonth(d.getMonth() + 1); setRef(d); };

  const openBadge = (ev) => {
    const resa = (data?.reservations || []).find((r) => r.id === ev.reservation_id);
    if (!resa) return;
    if (ev.kind === "menage") {
      setCleaningRes({ reservation: resa, cleaningId: ev.id, done: !!ev.done });
    } else if (ev.kind === "marche") {
      setMarketEvent({ event: ev, reservation: resa });
    } else if (ev.kind === "troisieme_lit") {
      nav(`/admin/gestion/voyageur/${resa.id}#troisieme-lit`);
    } else if (ev.kind === "breakfast") {
      nav(`/admin/gestion/voyageur/${resa.id}#petit-dejeuner`);
    } else {
      setEditEvent({ kind: ev.kind, event: ev, reservation: resa });
    }
  };

  const saveOverride = async (kind, payload) => {
    if (!editEvent) return;
    const resa = editEvent.reservation;
    const cur = resa.event_overrides || {};
    const nextOverrides = { ...cur, [kind]: { ...(cur[kind] || {}), ...payload } };
    await rgApi.updateReservation(resa.id, { event_overrides: nextOverrides });
    await load();
  };

  // Supprimer un jour de marché (sans toucher la réservation ni ses extras)
  const deleteMarket = async (resa) => {
    const cur = resa.event_overrides || {};
    const nextOverrides = { ...cur, marche: { ...(cur.marche || {}), hidden: true } };
    await rgApi.updateReservation(resa.id, { event_overrides: nextOverrides });
    setMarketEvent(null);
    await load();
  };

  // Réafficher un jour de marché précédemment supprimé
  const restoreMarket = async (resa) => {
    const cur = resa.event_overrides || {};
    const marche = { ...(cur.marche || {}) };
    delete marche.hidden;
    const nextOverrides = { ...cur, marche };
    await rgApi.updateReservation(resa.id, { event_overrides: nextOverrides });
    await load();
  };

  const hiddenMarkets = useMemo(
    () => (data?.reservations || []).filter((r) => r.event_overrides?.marche?.hidden),
    [data],
  );

  const refreshCleaningRes = async () => {
    if (!cleaningRes) return;
    try {
      const updated = await rgApi.getReservation(cleaningRes.reservation.id);
      setCleaningRes((prev) => (prev ? { ...prev, reservation: updated } : prev));
      await load();
    } catch (_e) { /* noop */ }
  };

  const updateCleaningReservation = async (patchData) => {
    if (!cleaningRes) return;
    await rgApi.updateReservation(cleaningRes.reservation.id, patchData);
    await refreshCleaningRes();
  };

  return (
    <div className="rg-page" data-testid="rg-calendar-page">
      <div className="rg-row" style={{ marginBottom: 12, justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div className="rg-row" style={{ gap: 8 }}>
          <button className="rg-btn ghost small" onClick={prevMonth} data-testid="rg-cal-prev"><ChevronLeft size={14} /></button>
          <h2 className="rg-h2">{MONTHS[ref.getMonth()]} {ref.getFullYear()}</h2>
          <button className="rg-btn ghost small" onClick={nextMonth} data-testid="rg-cal-next"><ChevronRight size={14} /></button>
        </div>
        <button className="rg-btn small" onClick={() => setClosedEdit("new")} data-testid="rg-cal-add-closed">
          <Plus size={13} /> Période fermée
        </button>
      </div>
      {!data ? (
        <div className="rg-loading">Chargement du calendrier…</div>
      ) : (
        <div className="rg-cal" data-testid="rg-cal-grid">
          <div className="rg-cal-head">
            {WEEKDAYS.map((w, i) => <div key={i}>{w}</div>)}
          </div>
          <div className="rg-cal-body">
            {weeks.map((weekDays, wi) => {
              const strips = weekStrips[wi] || [];
              const maxStack = strips.reduce((mx, s) => Math.max(mx, (s.stackIdx || 0) + 1), 0);
              const stripsHeight = maxStack * 22 + (maxStack > 0 ? 4 : 0);
              return (
                <div key={wi} className="rg-cal-week" style={{ "--strips-h": `${stripsHeight}px` }}>
                  {/* Cases jours */}
                  {weekDays.map((d, di) => {
                    const iso = toIso(d);
                    const inMonth = d.getMonth() === ref.getMonth();
                    const isMarket = marketSet.has(iso);
                    const events = dayEvents[iso] || [];
                    return (
                      <div
                        key={di}
                        className={`rg-cal-day ${!inMonth ? "out" : ""} ${isMarket ? "market" : ""} ${iso === today ? "today" : ""} ${!occupiedDays.has(iso) && inMonth ? "free" : ""}`}
                        data-testid={`rg-cal-day-${iso}`}
                      >
                        <div className="rg-cal-daynum">{d.getDate()}</div>
                        <div className="rg-cal-strips-space" />
                        <div className="rg-cal-emoji-row" data-testid={`rg-cal-emojis-${iso}`}>
                          {events.map((ev, ei) => {
                            const pending = (ev.kind === "breakfast" || ev.kind === "troisieme_lit") && ev.pending;
                            // Marché : afficher aussi les icônes des expériences
                            const marketExtras = ev.kind === "marche" ? (ev.extras || []) : [];
                            const EXP_ICONS = { bain_fleurs: "🌸", decor: "🌹", petit_dejeuner: "🥐" };
                            return (
                              <button
                                key={ei}
                                type="button"
                                className={`rg-cal-emoji-badge k-${ev.kind} ${ev.done ? "done" : ""} ${pending ? "pending" : ""}`}
                                onClick={() => openBadge(ev)}
                                title={`${ev.icon}${pending ? "❓" : ""} ${ev.kind} · ${firstName(ev.guest_name || ev.name || "")}${ev.time ? " · " + ev.time : ""}${pending ? " (formulaire manquant)" : ""}`}
                                data-testid={`rg-cal-badge-${ev.id}`}
                              >
                                <span className="rg-cal-emoji">
                                  {ev.icon}
                                  {pending && <span className="rg-cal-emoji-q">?</span>}
                                </span>
                                {ev.kind === "breakfast" && !pending && ev.time && (
                                  <span className="rg-cal-emoji-time" data-testid={`rg-cal-bf-time-${ev.id}`}>
                                    {ev.time.replace(":", "h")}
                                  </span>
                                )}
                                {marketExtras.length > 0 && (
                                  <span className="rg-cal-emoji-extras">
                                    {marketExtras.map((k) => EXP_ICONS[k]).filter(Boolean).join("")}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {/* Zone 3 — Événements personnels (chronologiques, discrets, en bas) */}
                        {(persoByDay[iso] || []).length > 0 && (
                          <div className="rg-cal-perso-row" data-testid={`rg-cal-perso-${iso}`}>
                            {(persoByDay[iso] || []).map((ic) => (
                              <span
                                key={ic.id}
                                className="rg-cal-perso"
                                title="Événement personnel"
                              >
                                {ic.time_start ? `PERSO · ${ic.time_start}` : "PERSO"}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Strips absolus dans la semaine (réservations 16h→12h + fermetures) */}
                  {strips.map((s, si) => {
                    const CELL = 100 / 7;
                    const HALF = CELL / 2;
                    let leftPct = s.startCol * CELL;
                    let widthPct = s.spanCols * CELL;

                    if (s.kind === "resa") {
                      // Résa : offset 50% jour arrivée (16h) et jour départ (12h)
                      if (s.isStart) { leftPct += HALF; widthPct -= HALF; }
                      if (s.isEnd)   { widthPct -= HALF; }
                      const people = s.resa.people || 0;
                      const label = `${firstName(s.resa.name).toUpperCase()}${people ? ` — ${people} PERS` : ""}`;
                      const needs3rdBed = people >= 3;
                      const tlType = s.resa.troisieme_lit_type;
                      const bedSuffix = needs3rdBed ? (tlType ? " 🛏️" : " 🛏️❓") : "";
                      const isPast = (s.resa.date_departure || "") < today;
                      return (
                        <button
                          key={si}
                          type="button"
                          onClick={() => nav(`/admin/gestion/voyageur/${s.resa.id}`)}
                          className={`rg-cal-strip ${s.isStart ? "start" : ""} ${s.isEnd ? "end" : ""} ${isPast ? "past" : ""}`}
                          style={{
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                            top: `${20 + (s.stackIdx || 0) * 22}px`,
                          }}
                          title={`${s.resa.name} · ${s.resa.date_arrival} 16h → ${s.resa.date_departure} 12h`}
                          data-testid={`rg-cal-strip-${s.resa.id}-${wi}`}
                        >
                          <span className="rg-cal-strip-label" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}{bedSuffix}</span>
                          </span>
                        </button>
                      );
                    }

                    if (s.kind === "closed") {
                      // Fermeture : pleine largeur des cases (inclus des deux côtés)
                      const cp = s.closed;
                      const label = `🔒 FERMÉ${cp.reason ? " — " + cp.reason.toUpperCase() : ""}`;
                      return (
                        <button
                          key={si}
                          type="button"
                          onClick={() => setClosedEdit(cp)}
                          className={`rg-cal-strip closed ${s.isStart ? "start" : ""} ${s.isEnd ? "end" : ""}`}
                          style={{
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                            top: `${20 + (s.stackIdx || 0) * 22}px`,
                          }}
                          title={`${cp.reason || "Fermé"} · ${cp.date_start} → ${cp.date_end}${cp.note ? " · " + cp.note : ""}`}
                          data-testid={`rg-cal-closed-${cp.id}-${wi}`}
                        >
                          <span className="rg-cal-strip-label">{label}</span>
                        </button>
                      );
                    }

                    if (s.kind === "off") {
                      // OFF iCloud (fermeture pro du refuge) : strip aligné zone 1, style rose.
                      return (
                        <div
                          key={si}
                          className={`rg-cal-strip off ${s.isStart ? "start" : ""} ${s.isEnd ? "end" : ""}`}
                          style={{
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                            top: `${20 + (s.stackIdx || 0) * 22}px`,
                          }}
                          title="OFF · Fermeture personnelle du refuge"
                          data-testid={`rg-cal-off-${s.off.id}-${wi}`}
                        >
                          <span className="rg-cal-strip-label">OFF</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Marchés supprimés — réaffichage rapide (n'affecte pas la réservation ni ses extras) */}
      {hiddenMarkets.length > 0 && (
        <div className="rg-row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }} data-testid="rg-hidden-markets">
          <span className="rg-muted" style={{ fontSize: 12 }}>🧺 Marchés supprimés :</span>
          {hiddenMarkets.map((r) => (
            <button
              key={r.id}
              type="button"
              className="rg-btn ghost small"
              onClick={() => restoreMarket(r)}
              data-testid={`rg-restore-market-${r.id}`}
              title="Réafficher ce jour de marché"
            >
              {firstName(r.name)} · Réafficher
            </button>
          ))}
        </div>
      )}

      {/* Modale ménage */}
      <CleaningModal
        open={!!cleaningRes}
        onClose={() => setCleaningRes(null)}
        reservation={cleaningRes?.reservation}
        cleaningId={cleaningRes?.cleaningId}
        done={cleaningRes?.done}
        onDone={refreshCleaningRes}
        onUpdateReservation={updateCleaningReservation}
      />

      {/* Modale édition d'événement (heure) */}
      <EventEditModal
        open={!!editEvent}
        onClose={() => setEditEvent(null)}
        event={editEvent?.event}
        reservation={editEvent?.reservation}
        onSaveOverride={saveOverride}
      />

      {/* Modale détails Marché (voyageur + expériences + liste achats) */}
      <MarketDetailsModal
        open={!!marketEvent}
        onClose={() => setMarketEvent(null)}
        event={marketEvent?.event}
        reservation={marketEvent?.reservation}
        onEditTime={() => {
          if (!marketEvent) return;
          setEditEvent({ kind: "marche", event: marketEvent.event, reservation: marketEvent.reservation });
          setMarketEvent(null);
        }}
        onDelete={() => { if (marketEvent) deleteMarket(marketEvent.reservation); }}
      />

      {/* Modale période fermée (création / édition) */}
      <ClosedPeriodModal
        open={!!closedEdit}
        onClose={() => setClosedEdit(null)}
        period={closedEdit === "new" ? null : closedEdit}
        onSaved={async () => { setClosedEdit(null); await load(); }}
      />
    </div>
  );
}
